import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { 
  Database, 
  Trash2, 
  RefreshCw, 
  HardDrive,
  Zap,
  AlertTriangle,
  CheckCircle,
  Server
} from 'lucide-react';
import { useToast } from '../components/Toast';

interface CacheStats {
  total_size: number;
  total_files: number;
  cached_files?: number;
  active_files?: number;
  expired_files?: number;
  total_size_mb?: number;
  instances: Record<string, {
    name?: string;
    size: number;
    files: number;
    last_modified: string | null;
  }>;
  hit_rate?: number | string;
  hit_rate_num?: number;
  miss_rate?: number;
}

interface Instance {
  instance_id: string;
  instance_name: string;
  name?: string;
  domain: string;
  cms_type: string;
  status: string;
}

export default function Cache() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [clearPattern, setClearPattern] = useState('');

  // Fetch cache stats
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery<CacheStats>({
    queryKey: ['cache-stats'],
    queryFn: async () => {
      const res = await fetch('/api/v1/cache/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch cache stats');
      return res.json();
    }
  });

  // Fetch instances
  const { data: instancesData } = useQuery<{ instances: Instance[], data?: Instance[] }>({
    queryKey: ['instances'],
    queryFn: async () => {
      const res = await fetch('/api/v1/instances', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch instances');
      return res.json();
    }
  });

  // Clear all cache
  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/v1/cache', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to clear cache');
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(`Cleared ${data.cleared} cache files`);
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
    },
    onError: () => {
      toast.error('Failed to clear cache');
    }
  });

  // Clear instance cache
  const clearInstanceMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`/api/v1/cache/${instanceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to clear instance cache');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Instance cache cleared');
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
    },
    onError: () => {
      toast.error('Failed to clear instance cache');
    }
  });

  // Clear by pattern
  const clearPatternMutation = useMutation({
    mutationFn: async ({ instanceId, pattern }: { instanceId: string; pattern: string }) => {
      const res = await fetch(`/api/v1/cache/${instanceId}/clear-pattern`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pattern })
      });
      if (!res.ok) throw new Error('Failed to clear cache by pattern');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Cache cleared by pattern');
      setClearPattern('');
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
    },
    onError: () => {
      toast.error('Failed to clear cache by pattern');
    }
  });

  // Warm cache
  const warmCacheMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      // Get common URLs to warm
      const urls = ['/', '/about', '/contact'];
      const res = await fetch(`/api/v1/cache/${instanceId}/warm`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ urls })
      });
      if (!res.ok) throw new Error('Failed to warm cache');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Cache warming started');
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
    },
    onError: () => {
      toast.error('Failed to warm cache');
    }
  });

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const instances = instancesData?.data || instancesData?.instances || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cache Management</h1>
          <p className="text-gray-600 mt-1">Monitor and manage kernel cache</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetchStats()}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => clearAllMutation.mutate()}
            disabled={clearAllMutation.isPending}
            className="btn-danger flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear All Cache
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <HardDrive className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Size</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '...' : formatBytes(stats?.total_size || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Database className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '...' : (stats?.total_files || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Hit Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '...' : (typeof stats?.hit_rate === 'string' ? stats.hit_rate : `${(stats?.hit_rate_num || 0).toFixed(1)}%`)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Miss Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '...' : `${(stats?.miss_rate || 0).toFixed(1)}%`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Instance Cache Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Instance Cache</h2>
        </div>
        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select an instance...</option>
              {instances.map((instance) => (
                <option key={instance.instance_id} value={instance.instance_id}>
                  {instance.instance_name || instance.name} - {instance.domain} ({instance.cms_type})
                </option>
              ))}
            </select>
            <button
              onClick={() => selectedInstance && clearInstanceMutation.mutate(selectedInstance)}
              disabled={!selectedInstance || clearInstanceMutation.isPending}
              className="btn-danger flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear Cache
            </button>
            <button
              onClick={() => selectedInstance && warmCacheMutation.mutate(selectedInstance)}
              disabled={!selectedInstance || warmCacheMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Warm Cache
            </button>
          </div>

          {/* Warm Cache Info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-1">What does Warm Cache do?</h4>
            <p className="text-sm text-blue-700">
              Cache warming pre-generates cached versions of common pages (/, /about, /contact) 
              so the first visitor doesn't experience slow load times. This is useful after 
              clearing cache or deploying updates.
            </p>
          </div>

          {/* Clear by Pattern */}
          {selectedInstance && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-sm text-amber-700">
                  <strong>Pattern examples:</strong> <code className="bg-amber-100 px-1 rounded">/blog/*</code>, 
                  <code className="bg-amber-100 px-1 rounded">/category/*</code>, 
                  <code className="bg-amber-100 px-1 rounded">*.html</code>
                </p>
              </div>
              <div className="flex gap-4">
              <input
                type="text"
                value={clearPattern}
                onChange={(e) => setClearPattern(e.target.value)}
                placeholder="Enter pattern (e.g., *.html, page_*)"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => clearPatternMutation.mutate({ instanceId: selectedInstance, pattern: clearPattern })}
                disabled={!clearPattern || clearPatternMutation.isPending}
                className="btn-secondary flex items-center gap-2"
              >
                Clear by Pattern
              </button>
              </div>
            </div>
          )}

          {/* Instance Cache Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Instance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Modified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats?.instances && Object.entries(stats.instances).map(([instanceId, data]) => (
                  <tr key={instanceId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Server className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{instanceId}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatBytes(data.size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {data.files.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {data.last_modified ? new Date(data.last_modified).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => clearInstanceMutation.mutate(instanceId)}
                        disabled={clearInstanceMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Clear
                      </button>
                    </td>
                  </tr>
                ))}
                {(!stats?.instances || Object.keys(stats.instances).length === 0) && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No cache data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
